import { useState } from "react"
import PropTypes from "prop-types"
import { useDispatch, useSelector } from "react-redux"
import EditableTagCell from "./EditableTagCell"
import UnavailableChipLabel from "ui/Blackout/UnavailableChipLabel"
import { getGuideBoatsListing } from "store/boats"
import {
  fetchAssignedEquipments,
  assignEquipmentsToBooking,
  unassignEquipmentFromBooking,
  selectAssignedEquipments,
  selectUnassignEquipmentLoading
} from "store/equipmentAssignments"
import styles from "./BookingTable.module.css"

// Same rule as the crew chip: an item a blackout has taken out says so where it sits.
const renderResourceLabel = (resource) =>
  resource.isUnavailable ? (
    <UnavailableChipLabel label={resource.name} reason={resource.unavailableReason} date={resource.unavailableDate} />
  ) : (
    resource.name
  )

// The picker listing returns one row per equipment (no asset-level ids). On assign we send
// only the equipment_id — the backend auto-assigns a concrete asset and echoes it back in
// assigned_assets, which is where the resulting chip gets its asset_id (see handleAdd).
const toResourceItem = (boat) => {
  const equipmentId = boat.equipment_id || boat.id
  return {
    id: equipmentId,
    name: boat.equipment_title || boat.name || "",
    equipmentId
  }
}

const ResourceCell = ({ initialResources, bookingId, emptyLabel, alwaysEditable, onCountChange, onAssignmentsChange }) => {
  const dispatch = useDispatch()
  const assignedEquipments = useSelector(selectAssignedEquipments)
  const removeLoading = useSelector(selectUnassignEquipmentLoading)
  const [groups, setGroups] = useState(null)
  const [isLoading, setIsLoading] = useState(false)

  const handleEditStart = () => {
    if (bookingId) dispatch(fetchAssignedEquipments(bookingId))
  }

  const handlePickerOpen = () => {
    if (!bookingId) return
    // A single filter=all call returns every equipment with an is_assigned flag; split it
    // into the two groups client-side (is_assigned → Available, else Other Equipment).
    setIsLoading(true)
    dispatch(getGuideBoatsListing(null, { booking_id: bookingId, filter: "all" }))
      .then((res) => {
        const rows = res?.data || []
        setGroups([
          { key: "available", label: "Available", items: rows.filter((boat) => boat.is_assigned).map(toResourceItem) },
          { key: "others", label: "Other Equipment", items: rows.filter((boat) => !boat.is_assigned).map(toResourceItem) }
        ])
      })
      .catch(() => {})
      .finally(() => setIsLoading(false))
  }

  const handleAdd = (items) => {
    if (!bookingId || !items.length) return []
    // Send equipment only — the backend auto-assigns the asset and returns it in assigned_assets.
    const assets = items.map((item) => ({ equipment_id: item.equipmentId }))
    const nameByEquipment = new Map(items.map((item) => [item.equipmentId, item.name]))
    // Build chips from the assets the backend actually assigned (carrying asset_id for later
    // unassign) so partially-failed selections don't show as chips.
    return dispatch(assignEquipmentsToBooking(bookingId, assets)).then((data) => {
      dispatch(fetchAssignedEquipments(bookingId))
      onAssignmentsChange?.()
      return (data?.assigned_assets || []).map((asset) => {
        const baseName = nameByEquipment.get(asset.equipment_id) || asset.equipment_title || asset.equipment_name || ""
        return {
          id: `${asset.equipment_id}-${asset.asset_id}`,
          name: asset.asset_number ? `${baseName} (${asset.asset_number})` : baseName,
          equipmentId: asset.equipment_id,
          assetId: asset.asset_id
        }
      })
    })
  }

  const handleRemove = (item) => {
    // Detail chips carry equipment_id + asset_id directly; picker-added chips already
    // carry them too. Fall back to the fetched assigned list only if missing.
    const assigned = assignedEquipments.find((eq) => eq.asset_id === item.assetId || eq.id === item.id)
    const equipmentId = item.equipmentId ?? assigned?.equipment_id
    const assetId = item.assetId ?? assigned?.asset_id
    if (bookingId && assetId != null) {
      // Resolve with the unassign result so EditableTagCell still drops the chip on success,
      // and notify the host (e.g. the equipment calendar) so it can refetch what it shows.
      return dispatch(unassignEquipmentFromBooking(bookingId, equipmentId, assetId)).then((res) => {
        onAssignmentsChange?.()
        return res
      })
    }
    return undefined
  }

  return (
    <EditableTagCell
      initialItems={initialResources}
      groups={groups}
      renderLabel={renderResourceLabel}
      addLabel="Add Resource"
      chipClassName={styles.resourceChip}
      onEditStart={handleEditStart}
      onPickerOpen={handlePickerOpen}
      onAdd={handleAdd}
      onRemove={handleRemove}
      loading={isLoading}
      removeLoading={removeLoading}
      emptyLabel={emptyLabel}
      alwaysEditable={alwaysEditable}
      onCountChange={onCountChange}
    />
  )
}

ResourceCell.propTypes = {
  initialResources: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]), name: PropTypes.string })
  ),
  bookingId: PropTypes.string,
  emptyLabel: PropTypes.string,
  alwaysEditable: PropTypes.bool,
  onCountChange: PropTypes.func,
  // Fired after an assign/unassign succeeds, for hosts rendering data that the change invalidates.
  onAssignmentsChange: PropTypes.func
}

ResourceCell.defaultProps = {
  initialResources: [],
  emptyLabel: "",
  alwaysEditable: false
}

export default ResourceCell
